import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { useRef } from "react";
import { toast } from "sonner";
import { userService } from "@/lib/api/services/user.service";
import type { Database } from "@/lib/supabase/supabase-types";

type User = Database["public"]["Tables"]["users"]["Row"];
type UserUpdate = Database["public"]["Tables"]["users"]["Update"];

export function useProfile() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  // Ref to track if we should suppress the update toast (e.g., during onboarding)
  const suppressUpdateToastRef = useRef(false);

  const profileQuery = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: () => userService.getMe(),
    enabled: !!user?.id,
    staleTime: 60 * 1000 * 10, // 10 minutes
    gcTime: 60 * 1000 * 15, // 15 minutes (formerly cacheTime)
    // refetchOnMount is 'always' by default, but with axios-level deduplication,
    // duplicate calls during login will be automatically deduplicated
    refetchOnWindowFocus: false, // Don't refetch on window focus to reduce unnecessary calls
  });

  const updateProfileMutation = useMutation({
    mutationFn: (updates: UserUpdate) => userService.updateMe(updates),
    onMutate: async (updates) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["profile", user?.id] });

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData<User>(["profile", user?.id]);

      // Optimistically update to the new value
      if (previousProfile) {
        queryClient.setQueryData<User>(["profile", user?.id], {
          ...previousProfile,
          ...updates,
        });
      }

      // Return a context object with the snapshotted value
      return { previousProfile };
    },
    onError: (err: any, _updates, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousProfile) {
        queryClient.setQueryData(["profile", user?.id], context.previousProfile);
      }
      console.error('Profile update failed:', err);
      toast.error(err.message || 'Failed to update profile. Please try again.');
    },
    onSuccess: (data) => {
      // Update with the actual server data
      queryClient.setQueryData(["profile", user?.id], data);
      // Also invalidate the user query used by role context
      queryClient.invalidateQueries({ queryKey: ["user", user?.id] });
      
      // Only show toast if not suppressed (e.g., during onboarding)
      if (!suppressUpdateToastRef.current) {
        toast.success('Profile updated successfully');
      }
      suppressUpdateToastRef.current = false;
    },
  });

  // Wrapper function that allows suppressing the toast
  const updateProfile = (updates: UserUpdate, options?: { suppressToast?: boolean }) => {
    suppressUpdateToastRef.current = options?.suppressToast ?? false;
    updateProfileMutation.mutate(updates);
  };

  // Async version for use with await
  const updateProfileAsync = async (updates: UserUpdate, options?: { suppressToast?: boolean }) => {
    suppressUpdateToastRef.current = options?.suppressToast ?? false;
    return await updateProfileMutation.mutateAsync(updates);
  };

  return {
    profile: profileQuery.data,
    isLoading: profileQuery.isLoading,
    error: profileQuery.error,
    updateProfile,
    updateProfileAsync,
    isUpdating: updateProfileMutation.isPending,
  };
}
